import React, { forwardRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SIZES } from '../../../fundamentals/sizes/sizes';
import {
  CheckState,
  IOptionCssProps,
  IOptionProps,
  OptionBase,
  OptionTexts,
  OptionType,
  OptionWrapper,
  CheckboxSpanBase,
  useSwitchBaseStateCss
} from '../Options';
import { useTestId } from '../../../hooks/useTestId/useTestId';
import { mergeRefs } from '../../../common/utils';
import { useOption } from '../useOption';
import { ReactComponent as CheckboxCheck } from './CheckboxCheck.svg';
import { ReactComponent as CheckboxIndeterminate } from './CheckboxIndeterminate.svg';

export interface ICheckboxProps extends IOptionProps {
  indeterminate?: boolean;
  bgOverride?: string;
}

const CheckboxIcon: React.FC<Pick<ICheckboxProps, 'indeterminate' | 'checked'>> = ({ indeterminate, checked }) => {
  if (!indeterminate && !checked) {
    return <></>;
  }

  return checked ? <CheckboxCheck /> : <CheckboxIndeterminate />;
};

const getCheckedState = ({ indeterminate, checked, disabled }: Pick<ICheckboxProps, 'indeterminate' | 'checked' | 'disabled'>) => {
  if (disabled) {
    return CheckState.DISABLED;
  }

  return checked || indeterminate ? CheckState.CHECKED : CheckState.UNCHECKED;
};

export type CheckboxBaseProps = ICheckboxProps & IOptionCssProps;

const CheckboxBase = styled(CheckboxSpanBase)<CheckboxBaseProps>`
  border-radius: ${({ theme }) => theme.brandBorderRadius.input};
  ${(props) => useSwitchBaseStateCss(getCheckedState(props))};

  background-color: ${({ bgOverride, checked, indeterminate, theme }) => {
    if (bgOverride && (checked || indeterminate)) {
      return bgOverride;
    }

    return theme.optionColours[checked ? CheckState.CHECKED : CheckState.UNCHECKED].switch;
  }};

  border: ${({ bgOverride, checked, indeterminate, theme }) => {
    if (bgOverride && (checked || indeterminate)) {
      return `1px solid ${bgOverride}`;
    }

    return checked || indeterminate ? theme.optionColours[CheckState.CHECKED].border : theme.optionColours.unchecked.border;
  }};

  * {
    color: ${({ theme, disabled }) => theme.optionColours[disabled ? CheckState.DISABLED : CheckState.UNCHECKED].text};
  }
`;

export const Checkbox = forwardRef<HTMLInputElement, ICheckboxProps>(
  (
    {
      indeterminate,
      disabled,
      size = SIZES.X_SMALL,
      onChange,
      defaultChecked,
      checked,
      label,
      rightAligned,
      helpText,
      helpTextAsError,
      wrapped,
      tabIndex,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, 'Checkbox');

    const { inputRef, focused, handleOnWrapperClick, handleOnBlur, handleOnTextClick, handleOnFocus, testIdLabel } = useOption({ wrapped, ...rest });

    const [uncontrolledChecked, setUncontrolledChecked] = useState(!!defaultChecked);

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      checked === undefined && indeterminate === undefined && setUncontrolledChecked(e.target.checked);
      onChange?.(e);
    };

    useEffect(() => {
      inputRef.current!.indeterminate = !!indeterminate;
    }, [indeterminate]);

    useEffect(() => {
      if (checked === undefined) {
        setUncontrolledChecked(uncontrolledChecked);
      } else {
        inputRef.current!.checked = checked;
      }
    }, [checked]);

    const Label = (
      <OptionTexts
        rightAligned={rightAligned}
        label={label}
        helpText={helpText}
        helpTextAsError={helpTextAsError}
        disabled={disabled}
        size={size}
        onClick={handleOnTextClick}
        {...generateTestId(testIdLabel)}
      />
    );

    return (
      <OptionWrapper
        focused={focused}
        size={size}
        rightAligned={rightAligned}
        wrapped={wrapped}
        disabled={disabled}
        onClick={handleOnWrapperClick}
        {...generateTestId('Wrapper')}
        {...rest}
      >
        {rightAligned && Label}
        <CheckboxBase
          wrapped={wrapped}
          focused={focused}
          checked={uncontrolledChecked || checked}
          optionSize={size}
          indeterminate={indeterminate}
          disabled={disabled}
          optionType={OptionType.CHECKBOX}
          bgOverride={rest.bgOverride} // Pass the bgOverride prop
          {...generateTestId('CheckboxBase')}
        >
          <OptionBase
            {...rest}
            type='checkbox'
            tabIndex={tabIndex}
            disabled={disabled}
            defaultChecked={defaultChecked}
            checked={checked}
            onChange={handleInputChange}
            ref={mergeRefs([inputRef, ref])}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            {...generateTestId(testIdLabel)}
          />
          <CheckboxIcon indeterminate={indeterminate} checked={uncontrolledChecked || checked} />
        </CheckboxBase>
        {!rightAligned && Label}
      </OptionWrapper>
    );
  }
);
